import { Badge } from '@artic/ui/badge';
import { CategoryBadge } from '@artic/ui/category-badge';
import { DataStateLabel } from '@artic/ui/data-state-label';
import { useFeatureFlag } from '@artic/ui/feature-flag';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@artic/ui/select';
import { Skeleton } from '@artic/ui/skeleton';
import { WORK_ORDER_STATUSES, WorkOrderStatusLabel } from '@artic/ui/work-order-status-label';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { parseAsInteger, useQueryState } from 'nuqs';

import {
  productBillOfOperationsQueryOptions,
  productDetailQueryOptions,
} from '../../features/products/queries';

export const Route = createFileRoute('/products/$productId')({
  staticData: {
    breadcrumb: (match, queryClient) => {
      const { productId } = match.params as { productId: string };
      const data = queryClient.getQueryData(productDetailQueryOptions(productId).queryKey);
      return data?.product_name ?? null;
    },
  },
  component: ProductDetailView,
});

function ProductDetailView() {
  const { productId } = Route.useParams();
  const workloadCategoriesEnabled = useFeatureFlag('workload_categories');
  const [version, setVersion] = useQueryState('boo_version', parseAsInteger);
  const { data: product, isLoading, isError } = useQuery(productDetailQueryOptions(productId));
  const {
    data: billOfOperations,
    isLoading: booLoading,
    isError: booError,
  } = useQuery(productBillOfOperationsQueryOptions(productId, version ?? undefined));

  const currentVersion =
    billOfOperations?.available_versions.find((v) => v.is_current)?.bill_of_operations_version ??
    billOfOperations?.bill_of_operations_version;

  if (isError) {
    return (
      <div className="text-destructive py-10 text-center text-sm">
        Failed to load product. Please try again.
      </div>
    );
  }

  if (isLoading || !product) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-48 rounded" />
        <Skeleton className="h-64 w-full rounded" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold">{product.product_name}</h1>
        <p className="text-muted-foreground mt-1 font-mono text-sm">{product.product_key}</p>
      </div>

      {/* Summary */}
      <div className="flex gap-4">
        {workloadCategoriesEnabled && (
          <div className="rounded-md border p-3">
            <span className="text-muted-foreground text-xs">Workload Category</span>
            <div className="mt-1 text-lg font-semibold">
              <Badge
                variant={
                  product.complexity_score >= 4
                    ? 'destructive'
                    : product.complexity_score >= 3
                      ? 'secondary'
                      : 'default'
                }
              >
                {product.complexity_score}
              </Badge>
            </div>
          </div>
        )}
        <div className="rounded-md border p-3">
          <span className="text-muted-foreground text-xs">Bill of Operations</span>
          <div className="mt-1">
            {product.bill_of_operations_id ? (
              <Badge variant="outline">Linked</Badge>
            ) : (
              <span className="text-muted-foreground text-sm">None</span>
            )}
          </div>
        </div>
      </div>

      {product.product_description && (
        <div>
          <h2 className="mb-1 text-sm font-medium">Description</h2>
          <p className="text-muted-foreground text-sm">{product.product_description}</p>
        </div>
      )}

      {/* Container selections */}
      <div>
        <h2 className="mb-2 text-sm font-medium">Container Selections</h2>
        {product.container_selections.length === 0 ? (
          <p className="text-muted-foreground text-sm">No container selections.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b text-left">
                  <th className="px-4 py-2 font-medium">Container</th>
                  <th className="px-4 py-2 font-medium">Selected Option</th>
                  <th className="px-4 py-2 font-medium">Code</th>
                  {workloadCategoriesEnabled && (
                    <>
                      <th className="px-4 py-2 text-right font-medium">Workload Category</th>
                      <th className="px-4 py-2 text-right font-medium">Weight</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {product.container_selections.map((sel) => (
                  <tr
                    key={sel.container_id}
                    className="hover:bg-muted/30 border-b transition-colors"
                  >
                    <td className="px-4 py-2 font-medium">{sel.container_name}</td>
                    <td className="px-4 py-2">{sel.option_name}</td>
                    <td className="px-4 py-2 font-mono text-xs">{sel.option_code}</td>
                    {workloadCategoriesEnabled && (
                      <>
                        <td className="px-4 py-2 text-right tabular-nums">{sel.option_score}</td>
                        <td className="text-muted-foreground px-4 py-2 text-right tabular-nums">
                          {sel.option_weight}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bill of Operations */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-sm font-medium">Bill of Operations</h2>
          {billOfOperations && (
            <>
              <span className="text-muted-foreground text-sm">
                {billOfOperations.bill_of_operations_name}
              </span>
              {billOfOperations.available_versions.length > 1 ? (
                <Select
                  value={String(
                    version ?? currentVersion ?? billOfOperations.bill_of_operations_version,
                  )}
                  onValueChange={(value) => {
                    const picked = Number(value);
                    void setVersion(picked === currentVersion ? null : picked);
                  }}
                >
                  <SelectTrigger size="sm" className="gap-1 font-mono text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {billOfOperations.available_versions.map((v) => (
                      <SelectItem
                        key={v.bill_of_operations_version}
                        value={String(v.bill_of_operations_version)}
                        className="font-mono text-xs"
                      >
                        v{v.bill_of_operations_version}
                        {v.is_current ? ' (current)' : ''} ·{' '}
                        {new Date(v.created_at).toLocaleDateString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="outline" className="font-mono text-xs">
                  v{billOfOperations.bill_of_operations_version}
                </Badge>
              )}
            </>
          )}
        </div>
        {billOfOperations && !billOfOperations.is_current_version && (
          <p className="text-muted-foreground mb-2 text-xs">
            Showing values as released in v{billOfOperations.bill_of_operations_version} — current
            product overrides are not applied.
          </p>
        )}
        {booLoading ? (
          <Skeleton className="h-32 w-full rounded" />
        ) : booError ? (
          <p className="text-destructive text-sm">Failed to load Bill of Operations.</p>
        ) : !billOfOperations ? (
          <p className="text-muted-foreground text-sm">
            This product has no Bill of Operations linked.
          </p>
        ) : billOfOperations.operations.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            The Bill of Operations exists but no operations have been released to it yet.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b text-left">
                  <th className="px-4 py-2 text-right font-medium">#</th>
                  <th className="px-4 py-2 font-medium">Operation</th>
                  <th className="px-4 py-2 font-medium">Category</th>
                  <th className="px-4 py-2 font-medium">Sub-category</th>
                  {workloadCategoriesEnabled && (
                    <th className="px-4 py-2 font-medium">Workload Category</th>
                  )}
                  <th className="px-4 py-2 text-right font-medium">Cycle Time</th>
                  <th className="px-4 py-2 text-right font-medium">Headcount</th>
                </tr>
              </thead>
              <tbody>
                {billOfOperations.operations.map((op) => {
                  const overridden =
                    op.applied_work_time !== op.operation_work_time ||
                    op.applied_headcount !== op.operation_headcount;
                  return (
                    <tr
                      key={op.operation_id}
                      className={`hover:bg-muted/30 border-b transition-colors ${
                        op.is_excluded ? 'text-muted-foreground line-through' : ''
                      }`}
                    >
                      <td className="text-muted-foreground px-4 py-2 text-right tabular-nums">
                        {op.operation_order}
                      </td>
                      <td className="px-4 py-2 font-medium">
                        {op.operation_name}
                        <Badge variant="outline" className="ml-2 font-mono text-xs">
                          v{op.operation_version}
                        </Badge>
                        {op.is_excluded && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            Excluded
                          </Badge>
                        )}
                        {overridden && !op.is_excluded && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            Override
                          </Badge>
                        )}
                      </td>
                      <td className="text-muted-foreground px-4 py-2">
                        {op.operation_category ?? '—'}
                      </td>
                      <td className="text-muted-foreground px-4 py-2">
                        {op.operation_sub_category ?? '—'}
                      </td>
                      {workloadCategoriesEnabled && (
                        <td className="px-4 py-2">
                          <span className="flex items-center gap-1.5">
                            <CategoryBadge category={op.applied_workload_category} />
                            <DataStateLabel state={op.data_state} />
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-2 text-right font-mono tabular-nums">
                        {op.applied_work_time}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {op.applied_headcount ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {billOfOperations && (
          <div className="mt-4">
            <h3 className="text-muted-foreground mb-2 text-xs font-medium">
              Work orders on v{billOfOperations.bill_of_operations_version}
            </h3>
            {billOfOperations.work_order_status_counts.length === 0 ? (
              <p className="text-muted-foreground text-sm">No work orders use this version.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {WORK_ORDER_STATUSES.filter((status) =>
                  billOfOperations.work_order_status_counts.some((c) => c.status === status),
                ).map((status) => {
                  const count =
                    billOfOperations.work_order_status_counts.find((c) => c.status === status)
                      ?.count ?? 0;
                  return (
                    <span
                      key={status}
                      className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1"
                    >
                      <WorkOrderStatusLabel status={status} />
                      <span className="text-sm font-medium tabular-nums">{count}</span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
